// @ts-check
/// <reference path="au-cart-types.js" />
/// <reference path="au-cart-utils.js" />

/**
 * @class
 */
class auCartDomHelper {
  /**
   * Updates the subtotal element in the DOM with the total price from the cart data.
   *
   * If cart data or the required DOM element is missing, an error is logged but not thrown.
   *
   * @param {ShopifyCart|undefined} cartData - The cart object containing the total price. If undefined or lacks `total_price`, update is skipped.
   */
  static updateSubTotal(cartData) {
    try {
      if (!cartData?.total_price) throw new Error('Cart data is not available');
      const total = auCartUtils.formatPrice(cartData.total_price);

      const subTotalElementSelector = 'p.totals__subtotal-value';
      const subTotalElement = document.querySelector(subTotalElementSelector);
      if (!subTotalElement) throw new Error('Cart data is not available');
      subTotalElement.innerHTML = total;
    } catch (error) {
      console.log('Error while updating subtotal', error);
    }
  }

  /**
   * Disables the rush fee UI controls and visually greys out the section.
   *
   * This method:
   * - Shows a warning message (if present)
   * - Disables the rush fee checkbox
   * - Greys out the rush fee wrapper element
   */
  static disableRushFee() {
    // Show warning message
    const rushFeeWarningDiv = /** @type {HTMLDivElement} */ (
      document.querySelector('div#rush-fee-warning')
    );
    if (rushFeeWarningDiv) {
      rushFeeWarningDiv.style.display = 'block';
    }

    // Disable checkbox
    const rushFeeCheckbox = /** @type {HTMLInputElement} */ (
      document.querySelector('#additional-rush-fee')
    );
    if (rushFeeCheckbox) {
      rushFeeCheckbox.disabled = true;
    }

    // Grey out wrapper
    const rushFeeWrapper = /** @type {HTMLDivElement} */ (
      document.querySelector('div.rush-fee')
    );
    if (rushFeeWrapper) {
      rushFeeWrapper.style.color = '#BBBBBB';
    }
  }

  /**
   * Enables the rush fee UI controls and resets the visual appearance.
   *
   * This method:
   * - Hides the warning message
   * - Enables the rush fee checkbox
   * - Resets the rush fee wrapper color to black
   */
  static enableRushFee() {
    // Hide warning message
    const rushFeeWarningDiv = /** @type {HTMLDivElement} */ (
      document.querySelector('div#rush-fee-warning')
    );
    if (rushFeeWarningDiv) {
      rushFeeWarningDiv.style.display = 'none';
    }

    // Enable checkbox
    const rushFeeCheckbox = /** @type {HTMLInputElement} */ (
      document.querySelector('#additional-rush-fee')
    );
    if (rushFeeCheckbox) {
      rushFeeCheckbox.disabled = false;
    }

    // Reset wrapper color
    const rushFeeWrapper = /** @type {HTMLDivElement} */ (
      document.querySelector('div.rush-fee')
    );
    if (rushFeeWrapper) {
      rushFeeWrapper.style.color = 'black';
    }
  }

  /**
   * Checks if white label is available for the current cart items.
   *
   * White label is NOT available if at least one item has `_isAlwaysWhiteLabel === true`.
   *
   * @param {Array<ShopifyCartItem>} items - Array of Shopify cart items.
   * @returns {boolean} True if white label is available, false otherwise.
   */
  static isAvailableWhiteLabel(items) {
    if (items.length === 0) return false;
    return !items.some((item) => {
      return item.properties._isAlwaysWhiteLabel === true;
    });
  }

  /**
   * Hides and disables the white-label checkbox UI element in the DOM.
   */
  static disableWhiteLabel() {
    const whiteLabelDiv = /** @type {HTMLDivElement} */ (
      document.querySelector('div.white-label')
    );
    if (whiteLabelDiv) {
      whiteLabelDiv.style.display = 'none';
    }
  }

  /**
   * Shows the white-label checkbox UI element by resetting its display style.
   */
  static enableWhiteLabel() {
    const whiteLabelDiv = /** @type {HTMLDivElement} */ (
      document.querySelector('div.white-label')
    );
    if (whiteLabelDiv) {
      whiteLabelDiv.style.display = '';
    }
  }

  /**
   * Retrieves the table row element associated with the given cart item key.
   *
   * @param {string} itemKey - The unique key of the cart item used in `data-key` attribute.
   * @returns {HTMLTableRowElement|null} The matching table row element, or null if not found.
   */
  static getItemRowElement(itemKey) {
    return /** @type {HTMLTableRowElement|null} */ (
      document.querySelector(`tr.cart-item[data-key='${itemKey}']`)
    );
  }

  static getWarningIcon() {
    return `
      <svg width="16" style="height:100%" height="17" viewBox="0 0 16 17" fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.00016 15.1663C11.6821 15.1663 14.6668 12.1816 14.6668 8.49967C14.6668 4.81778 11.6821 1.83301 8.00016 1.83301C4.31826 1.83301 1.3335 4.81778 1.3335 8.49967C1.3335 12.1816 4.31826 15.1663 8.00016 15.1663Z"
          stroke="#F15F3E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M8 5.83301V8.49967" stroke="#F15F3E" stroke-width="1.5" stroke-linecap="round"
          stroke-linejoin="round" />
        <path d="M8 11.167H8.00667" stroke="#F15F3E" stroke-width="1.5" stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    `;
  }

  /**
   * Disables cart-related UI elements to prevent user interaction during updates.
   *
   * @param {Array<HTMLButtonElement>|null} buttons - Buttons to disable.
   * @param {Array<HTMLInputElement>|null} inputs - Inputs to disable.
   */
  static disableCartUI(buttons, inputs) {
    if (buttons || inputs) {
      document.body.style.cursor = 'wait';
    }
    if (buttons) {
      buttons.forEach((btn) => (btn.disabled = true));
    }
    if (inputs) {
      inputs.forEach((input) => (input.disabled = true));
    }
  }

  /**
   * Re-enables cart-related UI elements after the update completes.
   *
   * @param {Array<HTMLButtonElement>|null} buttons - Buttons to re-enable.
   * @param {Array<HTMLInputElement>|null} inputs - Inputs to re-enable.
   */
  static enableCartUI(buttons, inputs) {
    if (buttons || inputs) {
      document.body.style.cursor = 'auto';
    }
    if (buttons) {
      buttons.forEach((btn) => (btn.disabled = false));
    }
    if (inputs) {
      inputs.forEach((input) => (input.disabled = false));
    }
  }

  /**
   * Removes cart item rows from the DOM.
   *
   * @param {Array<ShopifyCartItem>} products - Cart items to remove from the UI.
   */
  static removeCartItemsFromDOM(products) {
    for (const product of products) {
      const row = this.getItemRowElement(product.key);
      if (row) {
        row.remove();
      }
    }
  }

  /**
   * Replaces the `data-key` attribute value of all matching DOM elements with a new key.
   *
   * This is useful when cart item keys change (e.g., after updating properties),
   * and the UI needs to be updated accordingly.
   *
   * @param {string} itemKey - The current key of the line item to replace.
   * @param {string} newKey - The new key to assign to matching elements.
   */
  static replaceLineItemKey(itemKey, newKey) {
    const lineItemRowElements = /** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll(`[data-key="${itemKey}"]`)
    );
    if (lineItemRowElements.length > 0) {
      Array.from(lineItemRowElements).forEach((element) => {
        element.setAttribute('data-key', newKey);
      });
    }
  }

  /**
   * Disables the checkout button if it exists.
   * @param {boolean} state - True to disable the checkout button, false to enable it.
   */
  static setDisabledCheckout(state) {
    const checkoutButton = /** @type {HTMLButtonElement} */ (
      document.querySelector('button[name="checkout"]')
    );
    if (checkoutButton) {
      checkoutButton.disabled = state;
    }
  }

  /**
   * Adds extra side description to item row if initial load.
   * @param {boolean|null|undefined} isInitial
   * @param {ShopifyCartItem|undefined} extraSideItem
   * @param {ShopifyCartItem} item
   */
  static addExtraSideDescription(isInitial, extraSideItem, item) {
    if (!isInitial || !extraSideItem) return;
    const itemRowElement = auCartDomHelper.getItemRowElement(item.key);
    if (!itemRowElement) return;
    const descriptionList = itemRowElement.querySelector('dl');
    if (!descriptionList) return;
    descriptionList.innerHTML += `
      <div class="extra-side-extra-text product-option">
        <dt>Extra side: </dt>
        <dd>+${auCartUtils.formatPrice(extraSideItem.price)}</dd>
      </div>
    `;
  }

  /**
   * Change discount unit price in cart item row
   * @param {ShopifyCartItem} item
   */
  static changeDiscountUnitPrice(item) {
    const itemRowElement = auCartDomHelper.getItemRowElement(item.key);
    if (!itemRowElement) return;
    const unitPriceElements = Array.from(
      itemRowElement.querySelectorAll('.cart-item__prices .cart-item__discounted-prices dd.price.price--end bdi')
    );
    const discountUnitPrice = item.final_price;
    unitPriceElements.forEach((unitPriceElement) => {
      unitPriceElement.innerHTML = auCartUtils.formatPrice(discountUnitPrice);
    });
  }
}
